import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  AutocompleteOption,
  Avatar,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dropdown,
  FormControl,
  FormLabel,
  Tooltip,
  IconButton,
  Input,
  Link as JoyLink,
  Menu,
  MenuButton,
  MenuItem,
  Modal,
  ModalDialog,
  Option,
  Radio,
  RadioGroup,
  Select,
  Sheet,
  Stack,
  Switch,
  Typography,
} from '@mui/joy';
import { Link, useNavigate } from 'react-router-dom';
import { FaEllipsisV, FaEnvelope, FaMapMarkerAlt, FaPhone } from 'react-icons/fa';
import { IconWrapper } from '../../components/IconWrapper';
import { useCustomerStore } from '../../store/customerStore';
import { customerService, type Customer } from '../../services/customerService';
import { CustomerEditForm } from '../../components/CustomerEditForm';
import { customerExtraDetails } from '../../services/customerMapping';
import type { Project } from '../../types/project';
import { documentsService } from '../../modules/docs/services/documents.service';
import {
  cloneWorkspaceOptions,
  pickCloneSourceDocument,
  type CloneSourceKind,
  type CloneWorkspaceMode,
} from './workspaceQuote.helpers';
import {
  BILLING_DETAILS_REQUIRED_MESSAGE,
  BILLING_DETAILS_REQUIRED_TITLE,
  workspaceBillingDetailsAreValid,
  workspaceUsesBillingDetails,
} from './workspaceBillingIdentity';
import { cloneWorkspace } from './workspaceQuote.service';
import { brandForCustomer } from './workspaceBrand';
import { useBrands } from '../../hooks/useBrands';
import { selectableBrands } from '../../services/organisationSettings';
import { getFullWorkflowTasksForMode } from '../../utils/workflowUtils';

function customerInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

interface WorkspaceCustomerCardProps {
  project: Project;
  customer: Customer | null;
  allowChange?: boolean;
  forceChanging?: boolean;
  onProjectPatch?: (patch: Partial<Project>) => Promise<void>;
  requestLeave?: (proceed: () => void) => void;
}

const WorkspaceCustomerCard: React.FC<WorkspaceCustomerCardProps> = ({
  project,
  customer,
  allowChange = false,
  forceChanging = false,
  onProjectPatch,
  requestLeave,
}) => {
  const navigate = useNavigate();
  const { customers, loading: customersLoading, loadCustomers } = useCustomerStore();
  const [changing, setChanging] = useState(forceChanging || (allowChange && !customer));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneMode, setCloneMode] = useState<CloneWorkspaceMode>('copy_quote_design');
  const [cloneName, setCloneName] = useState(project.project_name || '');
  const [cloneBrandId, setCloneBrandId] = useState<string>('');
  const { brands } = useBrands();
  const [cloneBusy, setCloneBusy] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);
  const [cloneSourceKind, setCloneSourceKind] = useState<CloneSourceKind>('Quotation');
  const [supplyOnlyTarget, setSupplyOnlyTarget] = useState<boolean | null>(null);
  const [supplyOnlyBusy, setSupplyOnlyBusy] = useState(false);
  const [supplyOnlyError, setSupplyOnlyError] = useState<string | null>(null);

  // Keyed on the id: reloading the directory hands back a new customer object, which must not
  // trigger another reload or reset the picker's search text.
  const customerId = customer?.id ?? null;
  const hasCustomer = !!customer;
  useEffect(() => {
    if (!allowChange) return;
    if (changing || !hasCustomer) loadCustomers();
  }, [allowChange, changing, hasCustomer, loadCustomers]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pickerValue = useMemo(() => customer, [customerId]);

  useEffect(() => {
    if (forceChanging) setChanging(true);
  }, [forceChanging]);

  const selectCustomer = async (next: Customer | null) => {
    if (!allowChange || !onProjectPatch) return;
    if (!next || next.id === customer?.id) {
      setChanging(false);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await onProjectPatch({
        customer_id: next.id,
        customer_name: next.name,
        customer_email: next.email || '',
      });
      setChanging(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to change customer');
    } finally {
      setSaving(false);
    }
  };

  const openClone = () => {
    setCloneName(project.project_name || '');
    setCloneMode('copy_quote_design');
    setCloneError(null);
    setCloneSourceKind('Quotation');
    // Defaults to this workspace's brand while it can still be chosen, otherwise the customer's
    const current = brands.find((brand) => brand.id === project.organisation_id && brand.isSelectable);
    setCloneBrandId((current || brandForCustomer(brands, customer as any))?.id || '');
    setCloneOpen(true);
    documentsService.getDocumentsByProject(project.id)
      .then((docs) => {
        setCloneSourceKind(pickCloneSourceDocument(docs)?.kind === 'Estimate' ? 'Estimate' : 'Quotation');
      })
      .catch((err) => {
        console.warn('Failed to load workspace documents for clone', err);
      });
  };

  const requestClone = () => {
    if (requestLeave) requestLeave(openClone);
    else openClone();
  };

  const openEditCustomer = () => {
    const start = async () => {
      if (!customer?.id) return;
      setSaveError(null);
      try {
        const fresh = await customerService.getLocalCustomerById(customer.id);
        setEditingCustomer(fresh || customer);
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : 'Failed to load customer');
        setEditingCustomer(customer);
      }
    };
    if (requestLeave) requestLeave(() => { void start(); });
    else void start();
  };

  const saveEditedCustomer = async (id: string, updates: Record<string, unknown>) => {
    const updated = await customerService.updateLocalCustomer(id, updates);
    await loadCustomers();
    if (onProjectPatch) {
      const nextName = updated.name || project.customer_name;
      const nextEmail = updated.email || '';
      if (nextName !== (project.customer_name || '') || nextEmail !== (project.customer_email || '')) {
        await onProjectPatch({
          customer_name: nextName,
          customer_email: nextEmail,
        });
      }
    }
    setEditingCustomer(null);
  };

  const submitClone = async () => {
    setCloneBusy(true);
    setCloneError(null);
    try {
      const created = await cloneWorkspace({
        source: project,
        mode: cloneMode,
        projectName: cloneName,
        organisationId: cloneBrandId || null,
      });
      setCloneOpen(false);
      navigate(`/projects/${created.projectId}/workspace/quotes`);
    } catch (err) {
      setCloneError(err instanceof Error ? err.message : 'Failed to clone workspace');
    } finally {
      setCloneBusy(false);
    }
  };

  const openSupplyOnly = () => {
    setSupplyOnlyError(null);
    setSupplyOnlyTarget(!project.supply_only);
  };

  const submitSupplyOnly = async () => {
    if (supplyOnlyTarget == null || !onProjectPatch) return;
    setSupplyOnlyBusy(true);
    setSupplyOnlyError(null);
    try {
      // Same switch as the dashboard's Edit form: the checklist swaps install steps for delivery steps
      await onProjectPatch({
        supply_only: supplyOnlyTarget,
        workflow_tasks: getFullWorkflowTasksForMode(project.workflow_tasks || [], supplyOnlyTarget, project),
      });
      setSupplyOnlyTarget(null);
    } catch (err) {
      setSupplyOnlyError(err instanceof Error ? err.message : 'Failed to change the workspace type');
    } finally {
      setSupplyOnlyBusy(false);
    }
  };

  const cloneOptions = cloneWorkspaceOptions(cloneSourceKind);
  const selectedClone = cloneOptions.find((option) => option.value === cloneMode) || cloneOptions[0];
  const cloneBrandOptions = selectableBrands(brands);
  const cloneBrand = brands.find((brand) => brand.id === cloneBrandId) || null;
  const cloneChangesBrand = !!cloneBrand && !!project.organisation_id && cloneBrand.id !== project.organisation_id;
  const name = customer?.name || project.customer_name || '';
  const email = customer?.email || project.customer_email || '';
  const useBillingDetails = workspaceUsesBillingDetails(project);
  const billingDetailsValid = workspaceBillingDetailsAreValid({ project, customer });

  const setUseBillingDetails = async (next: boolean) => {
    if (!onProjectPatch) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onProjectPatch({ use_billing_details_for_documents: next });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to update billing setting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {saveError && <Alert color="danger" sx={{ mb: 1 }}>{saveError}</Alert>}

      {allowChange && (changing || !customer) && (
        <FormControl sx={{ mb: customer ? 1.5 : 0 }}>
          <FormLabel>Select customer</FormLabel>
          <Autocomplete<Customer, false, false, false>
            autoFocus={changing}
            placeholder="Search directory"
            options={
              customer && !customers.some((item) => item.id === customer.id)
                ? [customer, ...customers]
                : customers
            }
            loading={customersLoading}
            value={pickerValue}
            disabled={saving}
            onChange={(_, value) => { void selectCustomer(value); }}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(options, state) => {
              const needle = state.inputValue.trim().toLowerCase();
              if (!needle) return options;
              return options.filter((option) =>
                [option.name, option.email, option.phone]
                  .filter(Boolean)
                  .some((value) => String(value).toLowerCase().includes(needle))
              );
            }}
            slotProps={{
              listbox: { sx: { maxHeight: 280 } },
              input: {
                onKeyDown: (event) => {
                  if (event.key === 'Enter') event.stopPropagation();
                },
              },
            }}
            renderOption={(props, option) => (
              <AutocompleteOption {...props}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography level="body-sm">{option.name}</Typography>
                  {option.email && (
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                      {option.email}
                    </Typography>
                  )}
                </Box>
              </AutocompleteOption>
            )}
          />
          {saving && (
            <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>Saving…</Typography>
          )}
          {!customer && (
            <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
              New customers can be added from <Link to="/workspaces">Workspaces</Link>.
            </Typography>
          )}
        </FormControl>
      )}

      {!customer && !allowChange && (
        <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
          No customer assigned yet.
        </Typography>
      )}

      {customer && (
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ sm: 'flex-start' }}>
          <Avatar
            size="lg"
            variant="soft"
            color="primary"
            sx={{ fontWeight: 'lg', flexShrink: 0, width: 56, height: 56, fontSize: '1.1rem' }}
          >
            {customerInitials(name || '?')}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="flex-start" gap={1}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" alignItems="baseline" gap={1} flexWrap="wrap">
                  {name ? (
                    <JoyLink
                      level="title-lg"
                      href={`/customers/${encodeURIComponent(name)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ m: 0, fontWeight: 'lg' }}
                    >
                      {name}
                    </JoyLink>
                  ) : (
                    <Typography level="title-lg">Unnamed customer</Typography>
                  )}
                  {customer.company && (
                    <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                      {customer.company}
                    </Typography>
                  )}
                </Stack>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  gap={{ xs: 0.75, md: 3 }}
                  flexWrap="wrap"
                  sx={{ mt: 0.75 }}
                >
                  <Stack direction="row" gap={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box sx={{ color: 'neutral.400', display: 'flex' }}>
                      <IconWrapper icon={FaEnvelope} />
                    </Box>
                    {email ? (
                      <JoyLink href={`mailto:${email}`} level="body-sm" sx={{ overflowWrap: 'anywhere' }}>
                        {email}
                      </JoyLink>
                    ) : (
                      <Typography level="body-sm" sx={{ color: 'neutral.500' }}>No email</Typography>
                    )}
                  </Stack>
                  <Stack direction="row" gap={1} alignItems="center">
                    <Box sx={{ color: 'neutral.400', display: 'flex' }}>
                      <IconWrapper icon={FaPhone} />
                    </Box>
                    {customer.phone ? (
                      <JoyLink href={`tel:${customer.phone}`} level="body-sm">
                        {customer.phone}
                      </JoyLink>
                    ) : (
                      <Typography level="body-sm" sx={{ color: 'neutral.500' }}>No phone</Typography>
                    )}
                  </Stack>
                  <Stack direction="row" gap={1} alignItems="center" sx={{ minWidth: 0, flex: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ color: 'neutral.400', display: 'flex' }}>
                      <IconWrapper icon={FaMapMarkerAlt} />
                    </Box>
                    {customer.address ? (
                      <JoyLink
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(customer.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        level="body-sm"
                      >
                        {customer.address}
                      </JoyLink>
                    ) : (
                      <Typography level="body-sm" sx={{ color: 'neutral.500' }}>No address</Typography>
                    )}
                    <Tooltip
                      title="Quotes, estimates and invoices use billing identity when this is on. Job cards keep site details."
                      placement="top"
                      sx={{ maxWidth: 280 }}
                    >
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, ml: 0.5, flexShrink: 0 }}>
                        <Switch
                          size="sm"
                          checked={useBillingDetails}
                          disabled={saving || !onProjectPatch}
                          onChange={(event) => { void setUseBillingDetails(event.target.checked); }}
                          slotProps={{ input: { 'aria-label': 'Use billing details for documents' } }}
                        />
                        <Typography level="body-xs" sx={{ color: 'neutral.600', whiteSpace: 'nowrap' }}>
                          Use billing details for documents
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Stack>
                </Stack>
                {customerExtraDetails(customer).length > 0 && (
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    gap={{ xs: 0.5, md: 3 }}
                    flexWrap="wrap"
                    sx={{ mt: 0.75 }}
                  >
                    {customerExtraDetails(customer).map((item) => (
                      <Stack key={item.key} direction="row" gap={0.75} alignItems="baseline" sx={{ minWidth: 0 }}>
                        <Typography level="body-xs" sx={{ color: 'neutral.500', whiteSpace: 'nowrap' }}>
                          {item.label}
                        </Typography>
                        {item.href ? (
                          <JoyLink href={item.href} level="body-sm" sx={{ overflowWrap: 'anywhere' }}>
                            {item.value}
                          </JoyLink>
                        ) : (
                          <Typography level="body-sm">{item.value}</Typography>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                )}
                {useBillingDetails && !billingDetailsValid && (
                  <Alert color="danger" sx={{ mt: 1 }}>
                    <Typography level="title-sm">{BILLING_DETAILS_REQUIRED_TITLE}</Typography>
                    <Typography level="body-sm">{BILLING_DETAILS_REQUIRED_MESSAGE}</Typography>
                  </Alert>
                )}
              </Box>
              <Dropdown>
                <MenuButton
                  slots={{ root: IconButton }}
                  slotProps={{ root: { size: 'sm', variant: 'plain', 'aria-label': 'Workspace actions' } }}
                >
                  <IconWrapper icon={FaEllipsisV} />
                </MenuButton>
                <Menu placement="bottom-end">
                  {allowChange && (
                    changing ? (
                      <MenuItem
                        disabled={saving}
                        onClick={() => { setChanging(false); setSaveError(null); }}
                      >
                        Cancel
                      </MenuItem>
                    ) : (
                      <MenuItem onClick={() => setChanging(true)}>Change customer</MenuItem>
                    )
                  )}
                  <MenuItem onClick={openEditCustomer}>Update Customer Details</MenuItem>
                  {onProjectPatch && (
                    <MenuItem onClick={openSupplyOnly}>
                      {project.supply_only ? 'Change to Supply & install' : 'Change to Supply only'}
                    </MenuItem>
                  )}
                  <MenuItem onClick={requestClone}>Clone Workspace</MenuItem>
                </Menu>
              </Dropdown>
            </Stack>
          </Box>
        </Stack>
      )}

      <Modal open={cloneOpen} onClose={() => { if (!cloneBusy) setCloneOpen(false); }}>
        <ModalDialog sx={{ width: 'min(520px, 95vw)' }}>
          <DialogTitle>Clone Workspace</DialogTitle>
          <DialogContent>
            <Stack gap={1.5} sx={{ mt: 0.5 }}>
              <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                Creates a new workspace with a unique lifecycle ref. Final Survey, invoices, and job dates are not copied.
              </Typography>
              <FormControl>
                <FormLabel>Workspace name</FormLabel>
                <Input
                  value={cloneName}
                  disabled={cloneBusy}
                  onChange={(event) => setCloneName(event.target.value)}
                />
              </FormControl>
              {cloneBrandOptions.length > 0 && (
                <FormControl>
                  <FormLabel>Organisation</FormLabel>
                  <Select
                    value={cloneBrandId || null}
                    disabled={cloneBusy}
                    onChange={(_, value) => setCloneBrandId(String(value || ''))}
                  >
                    {cloneBrandOptions.map((brand) => (
                      <Option key={brand.id} value={brand.id}>{brand.name} ({brand.code})</Option>
                    ))}
                  </Select>
                  {cloneChangesBrand && (
                    <Typography level="body-xs" sx={{ color: 'neutral.600', mt: 0.5 }}>
                      The new workspace gets a {cloneBrand?.code}- number and {cloneBrand?.name} letterhead and emails.
                      This workspace keeps its number; archive it if it is no longer needed.
                    </Typography>
                  )}
                </FormControl>
              )}
              <FormControl>
                <FormLabel>What to copy</FormLabel>
                <RadioGroup
                  value={cloneMode}
                  onChange={(event) => setCloneMode(event.target.value as CloneWorkspaceMode)}
                  sx={{ gap: 0.75 }}
                >
                  {cloneOptions.map((option) => (
                    <Sheet
                      key={option.value}
                      variant="outlined"
                      sx={{
                        position: 'relative',
                        p: 1.25,
                        borderRadius: 'md',
                        borderColor: cloneMode === option.value ? 'primary.400' : 'divider',
                        bgcolor: cloneMode === option.value ? 'primary.50' : 'background.surface',
                      }}
                    >
                      <Radio
                        overlay
                        value={option.value}
                        disabled={cloneBusy}
                        label={option.label}
                        slotProps={{
                          radio: { sx: { opacity: 1 } },
                          input: { sx: { cursor: 'pointer' } },
                        }}
                      />
                    </Sheet>
                  ))}
                </RadioGroup>
                <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.75 }}>
                  {selectedClone.description}
                </Typography>
              </FormControl>
              {cloneError && <Alert color="danger">{cloneError}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" disabled={cloneBusy} onClick={() => setCloneOpen(false)}>
              Cancel
            </Button>
            <Button loading={cloneBusy} onClick={() => { void submitClone(); }}>
              Create workspace
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={supplyOnlyTarget != null} onClose={() => { if (!supplyOnlyBusy) setSupplyOnlyTarget(null); }}>
        <ModalDialog sx={{ maxWidth: 440 }}>
          <DialogTitle>{supplyOnlyTarget ? 'Change to Supply only' : 'Change to Supply & install'}</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5}>
              <Typography level="body-sm">
                {supplyOnlyTarget
                  ? 'The checklist will track delivery instead of installation. New quotes on this workspace start with no deposit, so the full balance is due.'
                  : 'The checklist will track installation instead of delivery. New quotes on this workspace start with a 40% deposit.'}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                Existing quotes keep their current deposit. Change it on the quote if needed.
              </Typography>
              {supplyOnlyError && <Alert color="danger">{supplyOnlyError}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" disabled={supplyOnlyBusy} onClick={() => setSupplyOnlyTarget(null)}>
              Cancel
            </Button>
            <Button loading={supplyOnlyBusy} onClick={() => { void submitSupplyOnly(); }}>
              Change
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <CustomerEditForm
        customer={editingCustomer}
        open={Boolean(editingCustomer)}
        onClose={() => setEditingCustomer(null)}
        onSave={saveEditedCustomer}
      />
    </Box>
  );
};

export default WorkspaceCustomerCard;
